/**
 * PhysicsEngine - public physics contracts, option types, and shared enums.
 */

import type { IPhysicsVehicle } from './IPhysicsVehicle.js';
import type { PhysicsMotionType } from './PhysicsTypes.js';
import type { Actor } from '../actors/Actor.js';
import type { BasePawnMovementComponent } from '../components/movement/BasePawnMovementComponent.js';
import type { PrimitiveComponent } from '../components/PrimitiveComponent.js';
import type { SceneComponent } from '../components/SceneComponent.js';
import type { CollisionProfile } from '../systems/collision/CollisionTypes.js';
import type * as THREE from 'three';

export enum PhysicsEngine {
    Disable = 'disable',
    Rapier = 'rapier',
}

/**
 * Configuration options for physics simulation
 */
export interface PhysicsOptions {
    /** Gravity vector (m/s²) */
    gravity: THREE.Vector3;
    /** Physics engine to use */
    engine: PhysicsEngine;
    /** Whether to enable debug visualization */
    debugVisualization?: boolean;
}

/**
 * Description of a scene component's state in the physics engine
 */
export interface PrimitiveComponentPhysicsState {
  motionType: PhysicsMotionType;
  isTrigger: boolean;
}

/**
 * Options for performing a hit test
 */
export interface HitTestOptions {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  maxDistance: number;
  stopOnFirstHit: boolean;
  collisionProfile?: CollisionProfile | null;
  ignoredActors?: Array<Actor>;
  ignoredComponents?: Array<SceneComponent>;
}

/**
 * Result of a hit test
 */
export interface HitTestResult {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  hitLocation: THREE.Vector3;
  hitNormal: THREE.Vector3;
  hitComponent: PrimitiveComponent | null;
  hitActor: Actor | null;
}

/**
 * Opaque handle to a physics body (abstracts away engine-specific types)
 */
export type PhysicsBodyHandle = string;

/**
 * Configuration for creating physics bodies directly
 */
export interface PhysicsBodyDesc {
  /** Body shape configuration */
  shape: PhysicsBodyShape;
  /** Mass (0 for static bodies) */
  mass: number;
  /** Initial world position */
  position: THREE.Vector3;
  /** Initial world rotation */
  rotation: THREE.Quaternion;
  /** Motion type */
  motionType: PhysicsMotionType;
  /** Whether the body starts enabled */
  enabled?: boolean;
}

/**
 * Shape configuration for physics bodies
 */
export interface PhysicsBodyShape {
  type: 'box' | 'sphere' | 'cylinder' | 'capsule';
  // For box
  size?: THREE.Vector3;
  // For sphere
  radius?: number;
  // For cylinder/capsule
  height?: number;
}

/**
 * Configuration options for character controllers
 * See https://rapier.rs/docs/user_guides/javascript/character_controller
 */
export interface CharacterControllerOptions {
  /** The gap the controller will leave between the character and its environment. */
  offset: number;

  /** The scale of the simulated gravity that is applied to the character controller.*/
  simulatedGravityScale: number;

  /** Whether to apply impulses to dynamic bodies. */
  applyImpulsesToDynamicBodies?: boolean;

  /** The mass of the character controller. */
  characterMass?: number;

  /** Whether to slide against obstacles. */
  slideEnabled?: boolean;

  /** The maximum angle (radians) between the floor's normal and the `up` vector that the character is able to climb. */
  maxSlopeClimbAngle?: number;

  /** The minimum angle (radians) between the floor's normal and the `up` vector before the character starts to slide down automatically. */
  minSlopeSlideAngle?: number;

  /** The distance threshold for snapping the character to the ground. If set, will enable snap to ground. */
  snapToGroundDistance?: number;

  /** Configuration for automatic step detection. If set, will enable auto stepping. */
  autoStepConfig?: {
    maxHeight: number,
    minWidth: number,
    includeDynamicBodies: boolean,
  }
}

/**
 * Scalar physics parameters that can be get/set on physics bodies
 */
export enum PhysicsScalarParam {
    /** Linear damping (0 = no damping, 1 = full damping) */
    LinearDamping = 'linearDamping',
    /** Angular damping (0 = no damping, 1 = full damping) */
    AngularDamping = 'angularDamping',
    /** Gravity scale multiplier (1 = normal gravity) */
    GravityScale = 'gravityScale',
}

/**
 * Vector physics parameters that can be get/set on physics bodies
 */
export enum PhysicsVectorParam {
    /** Linear velocity vector (m/s) */
    LinearVelocity = 'linearVelocity',
    /** Angular velocity vector (rad/s) */
    AngularVelocity = 'angularVelocity',
}

export type VectorArray = [/* x */ number, /* y */ number, /* z */ number];

export enum CollisionEventType {
  Started = 'started',
  Ended = 'ended',
}

export interface RawCollisionEvent {
  /** The first component involved in the collision */
  componentA: PrimitiveComponent;
  /** The second component involved in the collision */
  componentB: PrimitiveComponent;
  /** The collision point on the first component in world space */
  pointA: THREE.Vector3 | null;
  /** The collision normal pointing from A to B */
  normalA: THREE.Vector3 | null;
  /** The collision point on the second component in world space */
  pointB: THREE.Vector3 | null;
  /** The collision normal pointing from B to A */
  normalB: THREE.Vector3 | null;
  /** The relative velocity at the point of impact */
  relativeVelocity: number | null;
  /** Whether the collision is a trigger event */
  isTriggerEvent: boolean;
  /** The type of collision event */
  eventType: CollisionEventType;
}

/**
 * Contains information about a physics collision between two components
 */
export interface CollisionEvent {
  /** The component that was hit */
  thisComponent: PrimitiveComponent;
  /** The component that was hit */
  otherComponent: PrimitiveComponent;
  /** The collision point on this component in world space */
  thisPoint: THREE.Vector3 | null;
  /** The collision normal pointing from this component to the other component */
  thisNormal: THREE.Vector3 | null;
  /** The collision point on the other component in world space */
  otherPoint: THREE.Vector3 | null;
  /** The collision normal pointing from the other component to the this component */
  otherNormal: THREE.Vector3 | null;
  /** The relative velocity at the point of impact */
  relativeVelocity: number | null;
}

/**
 * Interface for physics engine implementations
 */
export interface IPhysicsEngine {
    /**
     * Initialize the physics engine.
     * @param options - Physics configuration options
     */
    create(options: PhysicsOptions): Promise<boolean>;

    /**
     * Get the physics engine options.
     * @returns Physics engine options
     */
    getOptions(): PhysicsOptions | null;

    /**
     * Clean up and destroy the physics engine.
     */
    destroy(): void;

    /**
     * Advance physics simulation by deltaTime.
     * @param deltaTime - Time step in seconds
     */
    tick(deltaTime: number): void;

    /**
     * Add a scene component to physics simulation.
     * @param component - Component to add
     * @returns Physics motion type if successful, null otherwise
     */
  addComponent(component: PrimitiveComponent): PhysicsMotionType | null;

    /**
     * Remove a scene component from physics simulation.
     * @param component - Component to remove
     */
  removeComponent(component: PrimitiveComponent): void;

  /**
   * Update transform of a physics component from its scene component.
   * @param component - Component to update
   */
  updateTransform(component: PrimitiveComponent): void;

    /**
     * Get scalar physics parameter for a component.
     * @param component - Target component
     * @param param - Parameter to get
     * @returns Parameter value or null if not applicable
     */
  getScalarParam(component: PrimitiveComponent, param: PhysicsScalarParam): number | null;

    /**
     * Set scalar physics parameter for a component.
     * @param component - Target component
     * @param param - Parameter to set
     * @param value - New parameter value
     * @returns True if successful, false otherwise
     */
  setScalarParam(component: PrimitiveComponent, param: PhysicsScalarParam, value: number): boolean;

    /**
     * Get vector physics parameter for a component.
     * @param component - Target component
     * @param param - Parameter to get
     * @returns Parameter value as [x,y,z] array or null if not applicable
     */
  getVectorParam(component: PrimitiveComponent, param: PhysicsVectorParam): VectorArray | null;

    /**
     * Set vector physics parameter for a component.
     * @param component - Target component
     * @param param - Parameter to set
     * @param value - New parameter value as [x,y,z] array
     * @returns True if successful, false otherwise
     */
  setVectorParam(component: PrimitiveComponent, param: PhysicsVectorParam, value: VectorArray): boolean;

    /**
     * Clear vector physics parameter for a component.
     * @param component - Target component
     * @param param - Parameter to clear
     * @returns True if successful, false otherwise
     */
  clearVectorParam(component: PrimitiveComponent, param: PhysicsVectorParam): boolean;

  /**
   * Gets all collision events that occurred in the last physics step.
   * @returns Array of collision info objects
   */
  getCollisionEvents(): RawCollisionEvent[];

  /**
   * Clear the stored collision events.
   */
  clearCollisionEvents(): void;

  /**
   * Toggles the physics debug renderer visibility.
   * @param enabled - Whether the debug renderer should be visible
   * @returns Whether the debug renderer is now enabled
   */
  setDebugRendererEnabled(enabled: boolean): boolean;

  /**
   * Checks if the physics debug renderer is enabled.
   * @returns Whether the debug renderer is currently enabled, or false if not available
   */
  isDebugRendererEnabled(): boolean;

  /* Create a character controller for a scene component.
    * @param movementComponent - Component to create controller for
    * @param options - Character controller options
    */
  createCharacterController(movementComponent: BasePawnMovementComponent, options: CharacterControllerOptions): boolean;

  /**
   * Remove a character controller for a scene component.
   * @param movementComponent - Component to remove controller for
   */
  removeCharacterController(movementComponent: BasePawnMovementComponent): void

  /**
   * Compute character movement for a scene component.
   * Applies the movement result directly to the physics body and returns both the grounded
   * state and the actual movement vector so callers can update the Three.js transform directly.
   * @param movementComponent - Component to compute movement for
   * @param componentToMove - Component to move
   * @param movement - Movement vector [x, y, z]
   * @param ignoreCollision - Whether to ignore collisions
   * @param immediateBodySync - When true, also calls setTranslation immediately (needed for
   *   non-final steps in a multi-input batch so subsequent KCC sweeps start from the correct
   *   position). Must be false on the last/only step to preserve Rapier's computed velocity
   *   for applyImpulsesToDynamicBodies.
   */
  computeCharacterMovement(movementComponent: BasePawnMovementComponent, componentToMove: PrimitiveComponent, movement: VectorArray, ignoreCollision: boolean, immediateBodySync?: boolean): { isGrounded: boolean; actualMovement: THREE.Vector3 };

  /**
   * Immediately teleport a component's physics body to the given position.
   * Updates both the body's current translation and its next kinematic target so the
   * change is visible to collision queries on the same tick without waiting for world.step().
   * Use for corrections, respawns, and replay starting-position resets.
   * @param component - The PrimitiveComponent whose body should be teleported
   * @param position - Target world position
   */
  teleportBody(component: PrimitiveComponent, position: THREE.Vector3): void;

  // Vehicle controller methods

  /**
   * Creates a vehicle controller for a scene component.
   * @param movementComponent - Component to create vehicle controller for
   * @param chassisComponent - The chassis scene component (usually a MeshComponent)
   * @returns Vehicle controller interface if successful, null if failed
   */
  createVehicle(movementComponent: BasePawnMovementComponent, chassisComponent: PrimitiveComponent): IPhysicsVehicle | null;

  /**
   * Configure collision filtering between two components.
   * @param componentA - First component
   * @param componentB - Second component
   * @param ignore - Whether componentA should ignore collisions with componentB
   */
  setComponentCollisionIgnore(componentA: PrimitiveComponent, componentB: PrimitiveComponent, ignore: boolean): boolean;

  /**
   * Creates a joint directly from physics body records.
   * @param type - Joint type
   * @param recordA - First physics body record
   * @param recordB - Second physics body record
   * @param anchorA - Anchor point on first body
   * @param anchorB - Anchor point on second body
   * @param axis - Optional axis for revolute/prismatic joints
   * @returns Joint ID if successful, null if failed
   */
  createJointFromRecords(type: any, recordA: any, recordB: any, anchorA: any, anchorB: any, axis?: any): string | null;

  /**
   * Removes a joint from the physics simulation.
   * @param jointId - The joint ID returned from createJoint
   * @returns True if the joint was successfully removed
   */
  removeJoint(jointId: string): boolean;

  // Direct physics body management methods

  /**
   * Create a physics body directly (not tied to a SceneComponent).
   * @param desc - Body descriptor with shape, mass, transform, etc.
   * @returns Physics body handle if successful, null otherwise
   */
  createPhysicsBody(desc: PhysicsBodyDesc): PhysicsBodyHandle | null;

  /**
   * Remove a physics body from simulation.
   * @param handle - Physics body handle
   * @returns True if successful
   */
  removePhysicsBody(handle: PhysicsBodyHandle): boolean;

  /**
   * Check if a physics body handle is valid.
   * @param handle - Physics body handle
   * @returns True if handle is valid
   */
  isValidPhysicsBody(handle: PhysicsBodyHandle): boolean;

  /**
   * Get world position of a physics body.
   * @param handle - Physics body handle
   * @returns World position or null if invalid handle
   */
  getPhysicsBodyPosition(handle: PhysicsBodyHandle): THREE.Vector3 | null;

  /**
   * Get world rotation of a physics body.
   * @param handle - Physics body handle
   * @returns World rotation or null if invalid handle
   */
  getPhysicsBodyRotation(handle: PhysicsBodyHandle): THREE.Quaternion | null;

  /**
   * Set world position of a physics body.
   * @param handle - Physics body handle
   * @param position - New world position
   * @returns True if successful
   */
  setPhysicsBodyPosition(handle: PhysicsBodyHandle, position: THREE.Vector3): boolean;

  /**
   * Set world rotation of a physics body.
   * @param handle - Physics body handle
   * @param rotation - New world rotation
   * @returns True if successful
   */
  setPhysicsBodyRotation(handle: PhysicsBodyHandle, rotation: THREE.Quaternion): boolean;

  /**
   * Apply an impulse to a physics body.
   * @param handle - Physics body handle
   * @param impulse - Impulse vector
   * @param worldSpace - Whether impulse is in world space (default: true)
   * @returns True if successful
   */
  applyImpulseToPhysicsBody(handle: PhysicsBodyHandle, impulse: THREE.Vector3, worldSpace?: boolean): boolean;

  /**
   * Enable or disable a physics body.
   * @param handle - Physics body handle
   * @param enabled - Whether the body should be enabled
   * @returns True if successful
   */
  setPhysicsBodyEnabled(handle: PhysicsBodyHandle, enabled: boolean): boolean;

  /**
   * Creates a joint between two physics bodies using handles.
   * @param type - Joint type
   * @param bodyA - First physics body handle
   * @param bodyB - Second physics body handle
   * @param anchorA - Anchor point on first body
   * @param anchorB - Anchor point on second body
   * @param axis - Optional axis for revolute/prismatic joints
   * @returns Joint ID if successful, null if failed
   */
  createJointFromBodies(
      type: any,
      bodyA: PhysicsBodyHandle,
      bodyB: PhysicsBodyHandle,
      anchorA: THREE.Vector3,
      anchorB: THREE.Vector3,
      axis?: THREE.Vector3
  ): string | null;

  /**
   * Perform a hit test to find the first collider hit.
   * @param options - The options for the hit test
   * @returns The list of hits
   */
  performHitTest(options: HitTestOptions): HitTestResult[];


    /**
     * Get the physics state of a scene component.
     * @param component - The scene component
     * @returns The physics state of the component
     */
  getComponentState(component: PrimitiveComponent): PrimitiveComponentPhysicsState | null;
}
